import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash, Scales } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { formatBRL, formatNum, computeTotals } from "@/lib/gado";

/**
 * Modo Balança (incorporado) — modo PRINCIPAL de pesagem, otimizado para uso no curral.
 * Peso e quantidade em fonte grande, fluxo sequencial (peso → quantidade → registrar)
 * e lista rolável das pesagens já registradas.
 * Reusa o estado (animals) do componente pai — sem duplicar dados.
 */
export function ScaleEntry({ animals, setAnimals, price_per_arroba, discount_kg }) {
  const [pesoDraft, setPesoDraft] = useState("");
  const [qtyDraft, setQtyDraft] = useState("");
  const pesoRef = useRef(null);
  const qtyRef = useRef(null);
  const listRef = useRef(null);

  const num = (v) => (v === "" || v == null ? null : parseFloat(String(v).replace(",", ".")));

  // Linhas já registradas (peso e quantidade preenchidos)
  const saved = useMemo(
    () =>
      (animals || [])
        .map((a, idx) => ({ idx, w: num(a.weight) || 0, q: parseInt(a.quantity || 0, 10) || 0 }))
        .filter((r) => r.w > 0 && r.q > 0),
    [animals]
  );

  const totals = useMemo(
    () =>
      computeTotals({
        animals: saved.map((r) => ({ number: r.idx + 1, weight: r.w, quantity: r.q })),
        price_per_arroba: num(price_per_arroba) || 0,
        discount_kg: num(discount_kg) || 0,
      }),
    [saved, price_per_arroba, discount_kg]
  );

  // Mantém a lista rolada até o final ao registrar novas linhas
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [saved.length]);

  // Auto-avança de Peso para Quantidade após pausa longa (1 min), quando o valor
  // parece completo (tem dígitos e não termina com separador decimal).
  useEffect(() => {
    if (!pesoDraft) return;
    if (/[.,]$/.test(pesoDraft)) return; // ainda digitando decimais
    const parsed = parseFloat(pesoDraft.replace(",", "."));
    if (!parsed || parsed <= 0) return;
    const t = setTimeout(() => {
      if (document.activeElement === pesoRef.current) {
        qtyRef.current?.focus();
      }
    }, 60000);
    return () => clearTimeout(t);
  }, [pesoDraft]);

  const commit = () => {
    const w = num(pesoDraft);
    const q = parseInt(qtyDraft || "0", 10);
    if (!w || w <= 0) {
      pesoRef.current?.focus();
      return false;
    }
    if (!q || q <= 0) {
      qtyRef.current?.focus();
      return false;
    }
    // Insere em animals — substitui a última linha vazia ou anexa nova linha
    setAnimals((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      const lastEmpty = !last || (!num(last.weight) && !parseInt(last.quantity || 0, 10));
      const row = { weight: String(w).replace(".", ","), quantity: String(q) };
      if (lastEmpty && next.length > 0) next[next.length - 1] = row;
      else next.push(row);
      next.push({ weight: "", quantity: "" }); // mantém linha vazia final p/ compatibilidade
      return next;
    });
    setPesoDraft("");
    setQtyDraft("");
    setTimeout(() => pesoRef.current?.focus(), 30);
    return true;
  };

  const removeRow = (idx) => {
    setAnimals((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) return [{ weight: "", quantity: "" }];
      return next;
    });
  };

  const inputCls =
    "mt-1.5 w-full bg-transparent border-b-2 border-white/25 focus:border-gado-goldLight outline-none font-cabinet font-extrabold text-4xl md:text-5xl text-white tabular-nums tracking-tight py-1.5 no-spin text-center placeholder:text-white/25";

  return (
    <div
      className="rounded-xl bg-gado-greenDark text-white p-4 md:p-5 space-y-4"
      data-testid="scale-entry"
    >
      {/* Totais ao vivo */}
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-gado-goldLight font-bold">
          <Scales size={13} weight="fill" /> Modo Balança
        </span>
        <span className="text-xs text-white/70 tabular-nums">
          {formatNum(totals.total_quantity, 0)} cab · {formatNum(totals.total_arrobas, 1)} @ ·{" "}
          <span className="text-gado-goldLight font-semibold">{formatBRL(totals.total_amount)}</span>
        </span>
      </div>

      {/* Inputs grandes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-gado-goldLight">Peso do gado (kg)</label>
          <input
            ref={pesoRef}
            type="text"
            inputMode="decimal"
            enterKeyHint="next"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="0,0"
            value={pesoDraft}
            onChange={(e) => setPesoDraft(e.target.value.replace(/[^0-9.,]/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                qtyRef.current?.focus();
              }
            }}
            data-testid="scale-peso"
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-gado-goldLight">Quantidade</label>
          <input
            ref={qtyRef}
            type="text"
            inputMode="numeric"
            enterKeyHint="done"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="0"
            value={qtyDraft}
            onChange={(e) => setQtyDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            data-testid="scale-qty"
            className={inputCls}
          />
        </div>
      </div>

      <Button
        onClick={commit}
        data-testid="scale-add"
        className="w-full h-12 rounded-xl bg-gado-gold hover:bg-gado-goldLight text-gado-ink font-bold text-base"
      >
        <Plus size={20} weight="bold" className="mr-2" /> Registrar pesagem
      </Button>

      {/* Lista de registradas */}
      <div className="border-t border-white/10 pt-3">
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 px-1">
          Registradas ({saved.length})
        </p>
        {saved.length === 0 ? (
          <p className="text-center text-white/40 text-sm py-4">
            Nenhuma pesagem ainda. Digite peso e quantidade acima.
          </p>
        ) : (
          <ul className="space-y-1 max-h-44 overflow-y-auto pr-1" ref={listRef} data-testid="scale-list">
            {saved.map((r, k) => (
              <li
                key={r.idx}
                data-testid={`scale-row-${k}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[10px] font-mono text-white/40 w-6 shrink-0">#{k + 1}</span>
                  <span className="font-mono font-semibold tabular-nums">
                    {formatNum(r.w, 1)}
                    <span className="text-white/50 text-xs"> kg</span>
                  </span>
                  <span className="text-white/30">·</span>
                  <span className="font-mono font-semibold tabular-nums">
                    {r.q}
                    <span className="text-white/50 text-xs"> cab</span>
                  </span>
                </div>
                <button
                  onClick={() => removeRow(r.idx)}
                  data-testid={`scale-remove-${k}`}
                  className="p-2 -mr-1 rounded-md text-white/50 hover:text-destructive hover:bg-white/5"
                  aria-label="Remover"
                >
                  <Trash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
